import React, { useCallback, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { RouteConfig } from '../../../../../shared/interfaces/routes-config.interface';
import { APP_ROUTES, PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { getDoubleBookings } from '../../../../../ReservationModule/shared/services/data.service';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { ReservationUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { ReservationChannel } from '../../../../../shared/enums/reservation-channel.enum';
import { BlockedDatesUiModel } from '../../../../../shared/ui-models/blocked-dates.ui-model';
import { cn } from '../../../../../utils/cn';
import { WarningIcon } from '../../../../../ui/Icons';

type DoubleBookingItem = {
  currentReservation: ReservationUiModel;
  otherReservation?: ReservationUiModel;
  blockedDate?: BlockedDatesUiModel;
};

const tableCellClassName = 'text-lg text-neutral-500';

const ChannelLogos = ({
  source,
  channel
}: {
  source?: ReservationSource | string;
  channel?: ReservationChannel | string;
}) => (
  <div className="flex items-center gap-2 text-[2rem]">
    {source === ReservationSource.BN_ADMIN ? (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/jpeg/admin.webp`}
        alt="Admin"
      />
    ) : source === ReservationSource.BN_APP ? (
      <img
        className="h-10 w-24 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/bn-app.png`}
        alt="BN App"
      />
    ) : source === ReservationSource.BN_WEBSITE ? (
      <img
        className="h-10 w-24 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/bn-website.png`}
        alt="BN Website"
      />
    ) : source === ReservationSource.CHANNEL_MANAGER ? (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/jpeg/rentals-united.jpeg`}
        alt="Channel Manager"
      />
    ) : (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/question-mark.png`}
        alt="Unknown"
      />
    )}
    <ChevronRightIcon fontSize="inherit" />
    {channel === ReservationChannel.AIRBNB ? (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/airbnb.png`}
        alt="Airbnb"
      />
    ) : channel === ReservationChannel.EXPEDIA ? (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/expedia.png`}
        alt="Expedia"
      />
    ) : channel === ReservationChannel.BOOKING ? (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/booking.png`}
        alt="Booking"
      />
    ) : channel === ReservationChannel.VRBO ? (
      <img
        className="h-10 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/vrbo.png`}
        alt="Vrbo"
      />
    ) : (
      <img
        className="h-10 w-20 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/birdnest-logo.png`}
        alt="Birdnest"
      />
    )}
  </div>
);

const DoubleBookingRow = ({ booking }: { booking: DoubleBookingItem }) => {
  const openReservation = (id?: string) => {
    if (!id) return;
    window.open(
      PUBLIC_PATH +
        (APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig).EDIT.FULL_PATH.replace(':id', id),
      '_blank'
    );
  };

  return (
    <TableRow
      className={cn(
        'transition-colors hover:bg-creams-300/40',
        booking.currentReservation.status === ReservationStatus.CHECKED_IN_STARTED && 'bg-amber-50'
      )}
    >
      <TableCell className={tableCellClassName}>
        {booking.currentReservation.unitName.replace(/(^.*\(|\).*$)/g, '')}
      </TableCell>
      <TableCell className={tableCellClassName}>
        <ChannelLogos
          source={booking.currentReservation.source as ReservationSource | string}
          channel={booking.currentReservation.channel as ReservationChannel | string}
        />
      </TableCell>
      <TableCell
        className={cn(tableCellClassName, 'cursor-pointer text-primary-500 hover:text-primary-700')}
        onClick={() => openReservation(booking.currentReservation.id)}
      >
        {booking.currentReservation.id}
      </TableCell>
      <TableCell className={tableCellClassName}>
        {booking.currentReservation.primaryGuestName}
      </TableCell>
      <TableCell className={tableCellClassName}>
        {new Date(booking.currentReservation.checkInDate).toDateString()}
      </TableCell>
      <TableCell className={tableCellClassName}>
        {new Date(booking.currentReservation.checkOutDate).toDateString()}
      </TableCell>
      <TableCell className={tableCellClassName}>
        <div className="h-8 w-3 rounded bg-[#566346]" />
      </TableCell>

      {booking.otherReservation ? (
        <>
          <TableCell className={tableCellClassName}>
            <ChannelLogos
              source={booking.otherReservation?.source as ReservationSource | string}
              channel={booking.otherReservation?.channel as ReservationChannel | string}
            />
          </TableCell>
          <TableCell
            className={cn(
              tableCellClassName,
              'cursor-pointer text-primary-500 hover:text-primary-700'
            )}
            onClick={() => openReservation(booking.otherReservation?.id)}
          >
            {booking.otherReservation.id}
          </TableCell>
          <TableCell className={tableCellClassName}>
            {booking.otherReservation.primaryGuestName}
          </TableCell>
          <TableCell className={tableCellClassName}>
            {new Date(booking.otherReservation.checkInDate).toDateString()}
          </TableCell>
          <TableCell className={tableCellClassName}>
            {new Date(booking.otherReservation.checkOutDate).toDateString()}
          </TableCell>
        </>
      ) : booking.blockedDate ? (
        <>
          <TableCell className={tableCellClassName}>
            <div className="flex items-center gap-2 text-[2rem]">
              <img
                className="h-10 w-10 object-contain"
                src={`${PUBLIC_PATH}/assets/pngs/blocked.png`}
                alt="Blocked"
              />
            </div>
          </TableCell>
          <TableCell
            className={cn(
              tableCellClassName,
              'cursor-pointer text-primary-500 hover:text-primary-700'
            )}
            onClick={() => {
              window.open(
                PUBLIC_PATH +
                  (APP_ROUTES.BLOCK_DATES_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH,
                '_blank'
              );
            }}
          >
            {booking.blockedDate.id}
          </TableCell>
          <TableCell className={tableCellClassName}>{booking.blockedDate?.description}</TableCell>
          <TableCell className={tableCellClassName}>
            {new Date(booking.blockedDate?.fromDate).toDateString()}
          </TableCell>
          <TableCell className={tableCellClassName}>
            {new Date(booking.blockedDate?.toDate).toDateString()}
          </TableCell>
        </>
      ) : (
        <></>
      )}
    </TableRow>
  );
};

export const DoubleBookings: React.FC = () => {
  const [doubleBookings, setDoubleBookings] = useState<DoubleBookingItem[]>([]);

  useEffect(() => {
    getDoubleBookings().then((response) => {
      if (!isErrorInfo(response)) {
        setDoubleBookings(response);
      }
    });
  }, []);

  if (!(doubleBookings?.length > 0)) return null;

  return (
    <div className="mb-12 w-full rounded-lg bg-white px-6 py-8 shadow-[0_6px_18px_rgba(23,34,64,0.08)]">
      <div className="mb-6 flex items-center gap-3 text-3xl font-medium font-gilroy-medium text-neutral-700">
        <WarningIcon /> Double Booking Check
      </div>

      {doubleBookings.length ? (
        <div className="max-h-[300px] w-full overflow-y-auto">
          <TableContainer component={Paper} className="shadow-none">
            <TableContainer aria-label="table">
              <TableHead>
                <TableRow>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Room Name
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Channel
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    #
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Title
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Check In
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Check Out
                  </TableCell>
                  <TableCell
                    className={cn(tableCellClassName, 'font-semibold text-gray-900')}
                  ></TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Channel
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    #
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Title
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Check In
                  </TableCell>
                  <TableCell className={cn(tableCellClassName, 'font-semibold text-gray-900')}>
                    Check Out
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doubleBookings?.length > 0 &&
                  doubleBookings?.map((booking) => (
                    <DoubleBookingRow
                      key={
                        booking.otherReservation?.id ||
                        booking.blockedDate?.id ||
                        booking.currentReservation.id
                      }
                      booking={booking}
                    />
                  ))}
              </TableBody>
            </TableContainer>
          </TableContainer>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-emerald-600">
          <img
            className="h-32 w-32 object-contain"
            src={`${PUBLIC_PATH}/assets/pngs/passed.png`}
            alt="no-data"
          />
          Everything looks Great!
        </div>
      )}
    </div>
  );
};
